import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!currentCategory) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">分类未找到</h1>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen">
        <section className="pt-24 pb-10 md:pt-28 md:pb-14 bg-background-100/60">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
                <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-700">{currentCategory.name}</span>
              </nav>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className={`${currentCategory.icon} text-2xl text-accent-600`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">
                    {currentCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-600">{currentCategory.description}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-4 md:py-6 bg-background-50 border-b border-background-200/60 sticky top-16 z-30">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto flex items-center gap-2 overflow-x-auto pb-1 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-accent-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="group rounded-xl overflow-hidden bg-background-100/70 border border-background-200/60 hover:border-primary-200 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3 mb-2.5">
                          <span className="text-xs text-foreground-500">{article.date}</span>
                          <span className="text-xs text-foreground-400">·</span>
                          <span className="text-xs text-foreground-500">{article.readTime}</span>
                        </div>
                        <h3 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center gap-2 mt-4 flex-wrap">
                          {article.tags.map((tag) => (
                            <span key={tag} className="px-2.5 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-sm text-foreground-500">该分类暂无文章</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory.name} - 爱液网页版`,
            description: currentCategory.description,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://aiye.com' },
                { '@type': 'ListItem', position: 2, name: currentCategory.name },
              ],
            },
          }),
        }}
      />
      <Footer />
    </>
  );
}